import React, { useRef } from 'react';
import { Box, Paper } from '@mui/material';
import {
  Chart,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

Chart.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler);



interface lineChartProps {
  data?: number[];
  label?: string[];
//   [key: string]: any;
}
const LineChart: React.FC<lineChartProps> = ({ data, label }) => {

  const labels = label || ['', 'SE : Self Egg', 'DE : Donor Egg'];
  const values = data || [0, 24.0, 36.8];

  // Build a vertical gradient using an offscreen canvas (returns a CanvasGradient or fallback color)
  function buildGradient(): CanvasGradient | string {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return 'rgba(137,142,169,0.08)';
      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, 'rgba(137,142,169,0.18)');
      gradient.addColorStop(1, 'rgba(137,142,169,0.02)');
      return gradient;
    } catch {
      return 'rgba(137,142,169,0.08)';
    }
  }

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Maturation Rate (in %)',
        data: values,
        borderColor: '#54566A',
        backgroundColor: buildGradient(),
        fill: true,
        tension: 0, 
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#54566A',
      },
    ],
  };

  // Plugin to draw a vertical marker line and highlight the selected point
  const verticalMarkerPlugin: Plugin<'line'> = {
    id: 'verticalMarker',
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      const xScale = chart.scales['x'];
      const yScale = chart.scales['y'];
      if (!xScale || !yScale) return;
      // draw vertical line at last data point
      const lastIndex = chart.data.labels ? chart.data.labels.length - 1 : 0;
      const x = xScale.getPixelForTick(lastIndex);
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.moveTo(x, chart.chartArea.top);
      ctx.lineTo(x, chart.chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      // draw highlighted circle and tooltip-like label above
      const dataset = chart.data.datasets[0];
      const val = (dataset.data as number[])[lastIndex];
      const y = yScale.getPixelForValue(val as number);
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = '#54566A';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // tooltip bubble
      const txt = `${val}%`;
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 1;
      ctx.font = '700 12px Montserrat';
      const padding = 6;
      const textWidth = ctx.measureText(txt).width;
      const boxWidth = textWidth + padding * 2;
      const boxHeight = 26;
      const bx = x - boxWidth / 2;
      const by = y - 28 - boxHeight;
      // rounded rect
      const r = 6;
      ctx.beginPath();
      ctx.moveTo(bx + r, by);
      ctx.lineTo(bx + boxWidth - r, by);
      ctx.quadraticCurveTo(bx + boxWidth, by, bx + boxWidth, by + r);
      ctx.lineTo(bx + boxWidth, by + boxHeight - r);
      ctx.quadraticCurveTo(bx + boxWidth, by + boxHeight, bx + boxWidth - r, by + boxHeight);
      ctx.lineTo(bx + r, by + boxHeight);
      ctx.quadraticCurveTo(bx, by + boxHeight, bx, by + boxHeight - r);
      ctx.lineTo(bx, by + r);
      ctx.quadraticCurveTo(bx, by, bx + r, by);
      ctx.closePath();
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.stroke();
      // text
      ctx.fillStyle = '#232323';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${val.toFixed(1)}%`, x, by + boxHeight / 2 - 2);

      ctx.restore();
    },
  };

  // Plugin to draw a horizontal reference line at 75%
  const horizontalReferencePlugin: Plugin<'line'> = {
    id: 'horizontalReference',
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      const yScale = chart.scales['y'];
      if (!yScale) return;
      const value = 75;
      const y = yScale.getPixelForValue(value as number);
      if (y === undefined) return;

      ctx.save();
      // dashed reference line
      ctx.strokeStyle = 'rgba(225,126,97,0.45)';
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(chart.chartArea.left, y);
      ctx.lineTo(chart.chartArea.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    },
  };

  const chartRef = useRef(null);
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
     
      <Box sx={{ width: '100%', height: { xs: 420, sm: 520, md: 640 } }}>
        <Line
          ref={chartRef}
          data={chartData}
          options={({
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { grid: { display: false }, ticks: { color: '#9E9E9E' } },
              y: {
                min: 0,
                max: 100,
                ticks: { stepSize: 10, color: '#9E9E9E' },
                grid: { color: 'rgba(0,0,0,0.04)', borderDash: [4, 6] },
              },
            },
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
          } as unknown) as ChartOptions<'line'>}
          plugins={[horizontalReferencePlugin, verticalMarkerPlugin]}
        />
      </Box>
    </Paper>
  );
};

export default LineChart;
